import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PLAN_TIERS, EMAIL_PROVIDERS } from "@/lib/tiers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Layers, ShieldCheck, Users, Loader2, Crown, Gauge, Mail } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { loadConfig } = useAuth();
  const [data, setData] = useState(null);
  const [mode, setMode] = useState("either");
  const [tier, setTier] = useState("pro");
  const [provider, setProvider] = useState("brevo");
  const [saving, setSaving] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setData(r.data);
      setMode(r.data.approval_mode);
      setTier(r.data.plan_tier);
      setProvider(r.data.email_provider);
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { approval_mode: mode });
      toast.success("Approval settings saved");
      setData((d) => ({ ...d, approval_mode: mode }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const savePlan = async () => {
    setSavingPlan(true);
    try {
      const { data: res } = await api.put("/settings", { plan_tier: tier, email_provider: provider });
      toast.success("Plan & delivery settings saved");
      setData((d) => ({ ...d, ...res }));
      loadConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSavingPlan(false); }
  };

  if (!data) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const planDirty = tier !== data.plan_tier || provider !== data.email_provider;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground"><Layers className="h-5 w-5" /></div>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Settings</h1>
          <p className="text-sm text-muted-foreground">Configure your plan, communications delivery, and leadership approvals.</p>
        </div>
      </div>

      {/* Plan tier */}
      <Card className="p-6">
        <h2 className="flex items-center gap-2 font-heading text-lg font-semibold"><Gauge className="h-5 w-5 text-primary" /> Plan &amp; features</h2>
        <p className="mt-1 text-sm text-muted-foreground">Your plan controls which features are turned on for your organization. Turning off the Publication Builder (Sunday News) is as simple as choosing a plan that doesn't include it.</p>
        <RadioGroup value={tier} onValueChange={setTier} className="mt-4 space-y-3">
          {PLAN_TIERS.map((p) => (
            <label key={p.id} className={`flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors ${tier === p.id ? "border-primary bg-primary/5" : "border-border"}`} data-testid={`tier-${p.id}`}>
              <RadioGroupItem value={p.id} className="mt-0.5" data-testid={`tier-${p.id}-radio`} />
              <div>
                <p className="font-medium">{p.label}
                  {p.recommended && <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">Most popular</span>}
                  {p.id === "network" && <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">Multi-unit (contact us)</span>}
                </p>
                <p className="text-sm font-medium text-foreground/70">{p.tagline}</p>
                <p className="text-sm text-muted-foreground">{p.desc}</p>
              </div>
            </label>
          ))}
        </RadioGroup>
      </Card>

      {/* Email delivery */}
      <Card className="p-6">
        <h2 className="flex items-center gap-2 font-heading text-lg font-semibold"><Mail className="h-5 w-5 text-primary" /> Newsletter delivery</h2>
        <p className="mt-1 text-sm text-muted-foreground">Choose the email platform your team uses. The Publication Builder's Export produces ready-to-paste HTML for the platform you pick.</p>
        <div className="mt-4 max-w-xs">
          <Label>Email platform</Label>
          <Select value={provider} onValueChange={setProvider}>
            <SelectTrigger className="mt-1.5" data-testid="email-provider-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {EMAIL_PROVIDERS.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`provider-${p.id}`}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button className="mt-5" onClick={savePlan} disabled={savingPlan || !planDirty} data-testid="save-plan-btn">
          {savingPlan ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save plan & delivery"}
        </Button>
      </Card>

      {/* Approval settings */}
      <Card className="p-6">
        <h2 className="flex items-center gap-2 font-heading text-lg font-semibold"><ShieldCheck className="h-5 w-5 text-primary" /> Leadership approval</h2>
        <p className="mt-1 text-sm text-muted-foreground">Both the Chapter President and Vice President always receive and can review every request.</p>
        <RadioGroup value={mode} onValueChange={setMode} className="mt-4 space-y-3">
          <label className={`flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors ${mode === "either" ? "border-primary bg-primary/5" : "border-border"}`} data-testid="mode-either">
            <RadioGroupItem value="either" className="mt-0.5" data-testid="mode-either-radio" />
            <div>
              <p className="font-medium">Either leader may decide <span className="ml-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">Recommended</span></p>
              <p className="text-sm text-muted-foreground">A decision from either the President or Vice President is sufficient. Once decided, the other retains view access but does not overwrite the decision. Prevents routine delays.</p>
            </div>
          </label>
          <label className={`flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors ${mode === "both" ? "border-primary bg-primary/5" : "border-border"}`} data-testid="mode-both">
            <RadioGroupItem value="both" className="mt-0.5" data-testid="mode-both-radio" />
            <div>
              <p className="font-medium">Both leaders required</p>
              <p className="text-sm text-muted-foreground">Both the President and Vice President must approve before a request moves to production. A denial by either leader stops the request. Use for sensitive or chapter-wide matters.</p>
            </div>
          </label>
        </RadioGroup>
        <Button className="mt-5" onClick={save} disabled={saving || mode === data.approval_mode} data-testid="save-settings-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save settings"}
        </Button>
      </Card>

      <Card className="p-6">
        <h2 className="flex items-center gap-2 font-heading text-lg font-semibold"><Users className="h-5 w-5 text-primary" /> Current leadership reviewers</h2>
        <p className="mt-1 text-sm text-muted-foreground">Permissions are assigned by office/role, so access transfers automatically when officers change.</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {data.leaders.map((l) => (
            <div key={l.role} className="flex items-center gap-3 rounded-lg border p-4" data-testid={`leader-${l.role}`}>
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/10 text-accent"><Crown className="h-5 w-5" /></div>
              <div>
                <p className="font-medium">{l.name}</p>
                <p className="text-xs text-muted-foreground">{l.title}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
